import type { Feature, FeatureCollection, LineString, MultiLineString, Point } from "geojson";
import { point as turfPoint, distance as turfDistance, nearestPoint } from "@turf/turf";
import dijkstra from "dijkstrajs";
import { releve_appuis, releve_liens } from "@/services/map.services";
import type { AnchorEndpoint, CableTronconMeta, TerrainRef } from "@/services/etude.services";

function normalizeInseeCode(raw: unknown): string {
  const code = String(raw ?? "").trim();
  if (!code) return "";
  return code.length === 4 ? `0${code}` : code;
}

/** Snap câble NRO → appui relevé (proposition automatique). */
export const DEFAULT_RELEVE_SNAP_TOLERANCE_M = 25;

/** Snap extrémité de lien relevé → appui (graphe / tronçon, comme G_snap QGIS). */
export const DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M = 2.5;

export type ReleveAppuiHit = {
  normId: string;
  label: string;
  insee: string;
  coordinates: [number, number];
};

export type SuggestAnchorsResult = {
  ok: boolean;
  depart: TerrainRef | null;
  arrivee: TerrainRef | null;
  departAppui: ReleveAppuiHit | null;
  arriveeAppui: ReleveAppuiHit | null;
  pathOk: boolean;
  pathNodeIds: string[];
  message: string;
};

/** Aligné sur `normalize_appui_id` dans tracerTroncon2026.py */
export function normalizeAppuiId(raw: unknown): string {
  if (raw == null) return "";
  let value = String(raw).trim();
  if (!value || value.toUpperCase() === "NULL") return "";
  const slash = value.split("/");
  value = slash[slash.length - 1].trim();
  value = value.replace(/^0+/, "");
  return value || "0";
}

function asFeatures(geo: unknown): Feature[] {
  if (!geo || typeof geo !== "object") return [];
  const g = geo as FeatureCollection;
  return Array.isArray(g.features) ? g.features : [];
}

function pickProp(props: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) {
    if (props[k] != null && String(props[k]).trim() !== "") return props[k];
    const upper = k.toUpperCase();
    if (props[upper] != null && String(props[upper]).trim() !== "") return props[upper];
    const lower = k.toLowerCase();
    if (props[lower] != null && String(props[lower]).trim() !== "") return props[lower];
  }
  return undefined;
}

export function getAppuiLabelFromProps(props: Record<string, unknown>): string {
  const raw = pickProp(props, [
    "NUM_APPUI",
    "num_appui",
    "NUMERO",
    "ID",
    "CODE_APPUI",
    "NOM_APPUI",
    "NOM",
  ]);
  return String(raw ?? "").trim();
}

export function getAppuiInseeFromProps(
  props: Record<string, unknown>,
  fallbackInsee?: string
): string {
  const raw = pickProp(props, [
    "INSEE_COMMU",
    "CODE_COMMU",
    "INSEE",
    "insee",
    "CODE_INSEE",
  ]);
  const code = normalizeInseeCode(raw);
  return code || normalizeInseeCode(fallbackInsee);
}

export function getLienExtremities(props: Record<string, unknown>): { x: string; y: string } {
  const x = normalizeAppuiId(
    pickProp(props, ["EXTREM_X", "extrem_x", "EXTREM_1", "EXTREM1", "DEPART", "depart"])
  );
  const y = normalizeAppuiId(
    pickProp(props, ["EXTREM_Y", "extrem_y", "EXTREM_2", "EXTREM2", "ARRIVEE", "arrivee"])
  );
  return { x, y };
}

function lineEndpoints(
  geometry: LineString | MultiLineString | null | undefined
): [[number, number], [number, number]] | null {
  if (!geometry) return null;
  let coords: number[][];
  if (geometry.type === "LineString") {
    coords = geometry.coordinates as number[][];
  } else if (geometry.type === "MultiLineString") {
    const line = geometry.coordinates[0];
    if (!line?.length) return null;
    coords = line as number[][];
  } else {
    return null;
  }
  if (coords.length < 2) return null;
  const start = coords[0] as [number, number];
  const end = coords[coords.length - 1] as [number, number];
  return [start, end];
}

function linkLengthMeters(feature: Feature): number {
  const geom = feature.geometry;
  if (!geom || (geom.type !== "LineString" && geom.type !== "MultiLineString")) return 1;
  const ends = lineEndpoints(geom as LineString | MultiLineString);
  if (!ends) return 1;
  const d = turfDistance(turfPoint(ends[0]), turfPoint(ends[1]), { units: "meters" });
  return Number.isFinite(d) && d > 0 ? d : 1;
}

export type ReleveGraph = {
  graph: Record<string, Record<string, number>>;
  nodeCount: number;
  edgeCount: number;
};

function addUndirectedEdge(
  graph: Record<string, Record<string, number>>,
  u: string,
  v: string,
  weight: number
): boolean {
  if (!u || !v || u === v) return false;
  if (!graph[u]) graph[u] = {};
  if (!graph[v]) graph[v] = {};
  const w = Number.isFinite(weight) && weight > 0 ? weight : 1;
  const added = graph[u][v] == null && graph[v][u] == null;
  if (graph[u][v] == null || w < graph[u][v]) graph[u][v] = w;
  if (graph[v][u] == null || w < graph[v][u]) graph[v][u] = w;
  return added;
}

export type BuildReleveGraphOptions = {
  appuisGeo?: unknown;
  /** Tolérance (m) extrémité de lien → appui le plus proche (défaut 2,5 m). 0 = désactivé. */
  endpointSnapToleranceM?: number;
};

/**
 * Graphe des liens relevé : arêtes EXTREM_X/Y + raccords géométriques si appuis proches
 * des extrémités de ligne (équivalent G + G_snap dans tracerTroncon2026.py).
 */
export function buildReleveGraphFromLiens(
  liensGeo: unknown,
  options?: BuildReleveGraphOptions
): ReleveGraph {
  const graph: Record<string, Record<string, number>> = {};
  let edgeCount = 0;

  for (const f of asFeatures(liensGeo)) {
    const props = (f.properties || {}) as Record<string, unknown>;
    const { x, y } = getLienExtremities(props);
    if (!x || !y || x === y) continue;
    const w = linkLengthMeters(f);
    if (addUndirectedEdge(graph, x, y, w)) edgeCount += 1;
  }

  const snapTol = options?.endpointSnapToleranceM ?? DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M;
  const appuisGeo = options?.appuisGeo;
  if (snapTol > 0 && appuisGeo && asFeatures(appuisGeo).length) {
    const appuis = indexReleveAppuis(appuisGeo);
    for (const f of asFeatures(liensGeo)) {
      const ends = lineEndpoints(f.geometry as LineString | MultiLineString | undefined);
      if (!ends) continue;
      const w = linkLengthMeters(f);
      const su = findNearestReleveAppui(ends[0], appuis, snapTol);
      const sv = findNearestReleveAppui(ends[1], appuis, snapTol);
      if (su && sv && su.normId !== sv.normId) {
        if (addUndirectedEdge(graph, su.normId, sv.normId, w)) edgeCount += 1;
      }
    }
  }

  return { graph, nodeCount: Object.keys(graph).length, edgeCount };
}

/** Raccorde un appui isolé au nœud du graphe le plus proche (≤ tolérance). */
export function connectOrphanAppuiToGraph(
  graph: Record<string, Record<string, number>>,
  appui: ReleveAppuiHit,
  allAppuis: ReleveAppuiHit[],
  toleranceM = DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M
): boolean {
  if (graph[appui.normId]) return true;

  const origin = turfPoint(appui.coordinates);
  let bestTarget: string | null = null;
  let bestDist = Infinity;

  for (const other of allAppuis) {
    if (other.normId === appui.normId) continue;
    if (!graph[other.normId]) continue;
    const d = turfDistance(origin, turfPoint(other.coordinates), { units: "meters" });
    if (d <= toleranceM && d < bestDist) {
      bestDist = d;
      bestTarget = other.normId;
    }
  }

  if (!bestTarget) return false;
  addUndirectedEdge(graph, appui.normId, bestTarget, Math.max(bestDist, 0.1));
  return true;
}

export function buildRelevePathGraph(
  liensGeo: unknown,
  appuisGeo: unknown,
  endpointSnapToleranceM = DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M
): { graph: Record<string, Record<string, number>>; appuis: ReleveAppuiHit[] } {
  const appuis = indexReleveAppuis(appuisGeo);
  const { graph } = buildReleveGraphFromLiens(liensGeo, {
    appuisGeo,
    endpointSnapToleranceM,
  });
  return { graph, appuis };
}

export function findShortestPath(
  graph: Record<string, Record<string, number>>,
  startNormId: string,
  endNormId: string
): string[] | null {
  if (!startNormId || !endNormId || startNormId === endNormId) return null;
  if (!graph[startNormId] || !graph[endNormId]) return null;
  try {
    return dijkstra.find_path(graph, startNormId, endNormId);
  } catch {
    return null;
  }
}

function connectedComponentSample(
  graph: Record<string, Record<string, number>>,
  start: string,
  max = 5
): string {
  const seen = new Set<string>();
  const queue = [start];
  seen.add(start);
  while (queue.length) {
    const n = queue.shift()!;
    const neighbors = graph[n];
    if (!neighbors) continue;
    for (const nb of Object.keys(neighbors)) {
      if (seen.has(nb)) continue;
      seen.add(nb);
      queue.push(nb);
    }
  }
  return [...seen].sort().slice(0, max).join(", ");
}

export function describePathFailure(
  graph: Record<string, Record<string, number>>,
  startNormId: string,
  endNormId: string
): string {
  if (!graph[startNormId]) {
    return `Appui départ (${startNormId}) absent des EXTREM_* des liens`;
  }
  if (!graph[endNormId]) {
    return `Appui arrivée (${endNormId}) absent des EXTREM_* des liens`;
  }
  const sample = connectedComponentSample(graph, startNormId);
  return `Pas de chemin sur le graphe relevé (composante départ ≈ ${sample || "?"})`;
}

export function indexReleveAppuis(
  appuisGeo: unknown,
  defaultInsee?: string
): ReleveAppuiHit[] {
  const hits: ReleveAppuiHit[] = [];
  for (const f of asFeatures(appuisGeo)) {
    if (f.geometry?.type !== "Point") continue;
    const coords = (f.geometry as Point).coordinates as [number, number];
    if (!Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) continue;
    const props = (f.properties || {}) as Record<string, unknown>;
    const label = getAppuiLabelFromProps(props);
    const normId = normalizeAppuiId(label);
    if (!normId || !label) continue;
    hits.push({
      normId,
      label,
      insee: getAppuiInseeFromProps(props, defaultInsee),
      coordinates: [coords[0], coords[1]],
    });
  }
  return hits;
}

export function findNearestReleveAppui(
  coord: [number, number],
  appuis: ReleveAppuiHit[],
  toleranceMeters = DEFAULT_RELEVE_SNAP_TOLERANCE_M
): ReleveAppuiHit | null {
  if (!appuis.length) return null;
  const origin = turfPoint(coord);
  const fc: FeatureCollection<Point> = {
    type: "FeatureCollection",
    features: appuis.map((a) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: a.coordinates },
      properties: { _normId: a.normId },
    })),
  };
  const nearest = nearestPoint(origin, fc);
  const d = turfDistance(origin, nearest, { units: "meters" });
  if (!Number.isFinite(d) || d > toleranceMeters) return null;
  const normId = String(nearest.properties?._normId ?? "");
  return appuis.find((a) => a.normId === normId) || null;
}

export function hitToTerrainRef(hit: ReleveAppuiHit, fallbackInsee?: string): TerrainRef {
  return {
    insee: hit.insee || normalizeInseeCode(fallbackInsee),
    nom: hit.label,
  };
}

export function hitToAnchorEndpoint(hit: ReleveAppuiHit, fallbackInsee?: string): AnchorEndpoint {
  const insee = hit.insee || normalizeInseeCode(fallbackInsee);
  return {
    source: "releve",
    type: "appui",
    id: hit.label,
    insee,
    label: hit.label,
    coordinates: hit.coordinates,
  };
}

export function suggestCableAnchorsFromReleve(
  cableFeature: Feature,
  options?: {
    appuisGeo?: unknown;
    liensGeo?: unknown;
    defaultInsee?: string;
    snapToleranceMeters?: number;
  }
): SuggestAnchorsResult {
  const appuisGeo = options?.appuisGeo ?? releve_appuis.value;
  const liensGeo = options?.liensGeo ?? releve_liens.value;
  const defaultInsee = options?.defaultInsee;
  const tolerance = options?.snapToleranceMeters ?? DEFAULT_RELEVE_SNAP_TOLERANCE_M;

  const empty: SuggestAnchorsResult = {
    ok: false,
    depart: null,
    arrivee: null,
    departAppui: null,
    arriveeAppui: null,
    pathOk: false,
    pathNodeIds: [],
    message: "",
  };

  if (!asFeatures(appuisGeo).length) {
    return { ...empty, message: "Couche appuis relevé absente" };
  }
  if (!asFeatures(liensGeo).length) {
    return { ...empty, message: "Couche liens relevé absente" };
  }

  const ends = lineEndpoints(
    cableFeature.geometry as LineString | MultiLineString | undefined
  );
  if (!ends) {
    return { ...empty, message: "Géométrie câble invalide" };
  }

  const appuis = indexReleveAppuis(appuisGeo, defaultInsee);
  const depHit = findNearestReleveAppui(ends[0], appuis, tolerance);
  const arrHit = findNearestReleveAppui(ends[1], appuis, tolerance);

  if (!depHit && !arrHit) {
    return {
      ...empty,
      message: `Aucun appui relevé à moins de ${tolerance} m des extrémités`,
    };
  }
  if (!depHit || !arrHit) {
    const side = !depHit ? "départ" : "arrivée";
    const partial = depHit || arrHit;
    return {
      ok: false,
      depart: depHit ? hitToTerrainRef(depHit, defaultInsee) : null,
      arrivee: arrHit ? hitToTerrainRef(arrHit, defaultInsee) : null,
      departAppui: depHit,
      arriveeAppui: arrHit,
      pathOk: false,
      pathNodeIds: [],
      message: `Appui ${side} introuvable (tolérance ${tolerance} m)`,
    };
  }
  if (depHit.normId === arrHit.normId) {
    return {
      ok: false,
      depart: hitToTerrainRef(depHit, defaultInsee),
      arrivee: hitToTerrainRef(arrHit, defaultInsee),
      departAppui: depHit,
      arriveeAppui: arrHit,
      pathOk: false,
      pathNodeIds: [],
      message: "Même appui relevé aux deux extrémités du câble",
    };
  }

  const { graph } = buildReleveGraphFromLiens(liensGeo, {
    appuisGeo,
    endpointSnapToleranceM: DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M,
  });
  const pathNodeIds = findShortestPath(graph, depHit.normId, arrHit.normId);
  const pathOk = !!pathNodeIds?.length;

  const depart = hitToTerrainRef(depHit, defaultInsee);
  const arrivee = hitToTerrainRef(arrHit, defaultInsee);

  if (pathOk) {
    return {
      ok: true,
      depart,
      arrivee,
      departAppui: depHit,
      arriveeAppui: arrHit,
      pathOk: true,
      pathNodeIds: pathNodeIds || [],
      message: `Chemin relevé OK (${(pathNodeIds?.length || 1) - 1} tronçon(s), ${pathNodeIds?.length || 0} appui(s))`,
    };
  }

  return {
    ok: true,
    depart,
    arrivee,
    departAppui: depHit,
    arriveeAppui: arrHit,
    pathOk: false,
    pathNodeIds: [],
    message: describePathFailure(graph, depHit.normId, arrHit.normId),
  };
}

/** Trouve un appui relevé par INSEE + nom (correspondance terrain). */
export function findReleveAppuiByTerrainRef(
  ref: TerrainRef,
  appuisGeo?: unknown,
  defaultInsee?: string
): ReleveAppuiHit | null {
  const insee = normalizeInseeCode(ref.insee);
  const nom = String(ref.nom ?? "").trim();
  if (!nom) return null;
  const normNom = normalizeAppuiId(nom);
  const appuis = indexReleveAppuis(appuisGeo ?? releve_appuis.value, defaultInsee);
  for (const a of appuis) {
    const labelMatch = a.label === nom || a.normId === normNom;
    if (!labelMatch) continue;
    if (insee && a.insee && a.insee !== insee) continue;
    return a;
  }
  return null;
}

function tronconMetaNow(
  partial: Omit<CableTronconMeta, "checkedAt"> & { checkedAt?: string }
): CableTronconMeta {
  return {
    ...partial,
    checkedAt: partial.checkedAt || new Date().toISOString(),
  };
}

/** Évalue le chemin relevé entre départ et arrivée enregistrés (INSEE + nom). */
export function evaluateTronconFromTerrainAnchors(
  depart: AnchorEndpoint | null | undefined,
  arrivee: AnchorEndpoint | null | undefined,
  options?: { appuisGeo?: unknown; liensGeo?: unknown; defaultInsee?: string }
): CableTronconMeta {
  if (!asFeatures(options?.appuisGeo ?? releve_appuis.value).length) {
    return tronconMetaNow({
      status: "incomplet",
      message: "Relevé appuis non chargé",
      pathNodeIds: [],
      linkCount: 0,
    });
  }
  if (!asFeatures(options?.liensGeo ?? releve_liens.value).length) {
    return tronconMetaNow({
      status: "incomplet",
      message: "Relevé liens non chargé",
      pathNodeIds: [],
      linkCount: 0,
    });
  }
  if (!depart?.insee || (!depart.label && !depart.id)) {
    return tronconMetaNow({
      status: "incomplet",
      message: "Départ non renseigné",
      pathNodeIds: [],
      linkCount: 0,
    });
  }
  if (!arrivee?.insee || (!arrivee.label && !arrivee.id)) {
    return tronconMetaNow({
      status: "incomplet",
      message: "Arrivée non renseignée",
      pathNodeIds: [],
      linkCount: 0,
    });
  }

  const depRef: TerrainRef = {
    insee: normalizeInseeCode(depart.insee),
    nom: String(depart.label || depart.id).trim(),
  };
  const arrRef: TerrainRef = {
    insee: normalizeInseeCode(arrivee.insee),
    nom: String(arrivee.label || arrivee.id).trim(),
  };

  const depHit = findReleveAppuiByTerrainRef(
    depRef,
    options?.appuisGeo,
    options?.defaultInsee
  );
  const arrHit = findReleveAppuiByTerrainRef(
    arrRef,
    options?.appuisGeo,
    options?.defaultInsee
  );

  if (!depHit) {
    return tronconMetaNow({
      status: "echec",
      message: `Appui départ introuvable sur le relevé (${depRef.insee} / ${depRef.nom})`,
      pathNodeIds: [],
      linkCount: 0,
    });
  }
  if (!arrHit) {
    return tronconMetaNow({
      status: "echec",
      message: `Appui arrivée introuvable sur le relevé (${arrRef.insee} / ${arrRef.nom})`,
      pathNodeIds: [],
      linkCount: 0,
    });
  }
  if (depHit.normId === arrHit.normId) {
    return tronconMetaNow({
      status: "echec",
      message: "Départ et arrivée identiques sur le relevé",
      pathNodeIds: [depHit.normId],
      linkCount: 0,
    });
  }

  const liensGeo = options?.liensGeo ?? releve_liens.value;
  const appuisGeo = options?.appuisGeo ?? releve_appuis.value;
  const snapTol = DEFAULT_LINK_ENDPOINT_SNAP_TOLERANCE_M;
  const baseGraph = buildReleveGraphFromLiens(liensGeo).graph;
  const { graph, appuis } = buildRelevePathGraph(liensGeo, appuisGeo, snapTol);

  connectOrphanAppuiToGraph(graph, depHit, appuis, snapTol);
  connectOrphanAppuiToGraph(graph, arrHit, appuis, snapTol);

  const pathOnBase = findShortestPath(baseGraph, depHit.normId, arrHit.normId);
  const pathNodeIds = findShortestPath(graph, depHit.normId, arrHit.normId);
  if (pathNodeIds?.length) {
    const linkCount = Math.max(0, pathNodeIds.length - 1);
    const viaSnap = !pathOnBase?.length;
    const snapNote = viaSnap ? ` · snap ${snapTol} m` : "";
    return tronconMetaNow({
      status: "ok",
      message: `Tronçon OK (${linkCount} lien(s), ${pathNodeIds.length} appui(s)${snapNote})`,
      pathNodeIds,
      linkCount,
    });
  }

  const depInBase = !!baseGraph[depHit.normId];
  const arrInBase = !!baseGraph[arrHit.normId];
  let detail = describePathFailure(graph, depHit.normId, arrHit.normId);
  if (!depInBase) {
    detail = `Appui départ hors EXTREM_* (tolérance snap ${snapTol} m)`;
  } else if (!arrInBase) {
    detail = `Appui arrivée hors EXTREM_* (tolérance snap ${snapTol} m)`;
  }

  return tronconMetaNow({
    status: "echec",
    message: detail,
    pathNodeIds: [],
    linkCount: 0,
  });
}

export function tronconMetaForIgnored(message = "Ignoré par l'utilisateur"): CableTronconMeta {
  return tronconMetaNow({ status: "ignore", message, pathNodeIds: [], linkCount: 0 });
}

function findLienGeometryBetween(
  u: string,
  v: string,
  liensGeo: unknown
): LineString | MultiLineString | null {
  for (const f of asFeatures(liensGeo)) {
    const geom = f.geometry;
    if (geom?.type !== "LineString" && geom?.type !== "MultiLineString") continue;
    const props = (f.properties || {}) as Record<string, unknown>;
    const { x, y } = getLienExtremities(props);
    if ((x === u && y === v) || (x === v && y === u)) {
      return geom as LineString | MultiLineString;
    }
  }
  return null;
}

/** Segments du chemin tronçon (géométrie des liens relevé ou corde entre appuis). */
export function buildTronconPathFeatures(
  pathNodeIds: string[],
  options?: { liensGeo?: unknown; appuisGeo?: unknown; defaultInsee?: string }
): Feature[] {
  if (!pathNodeIds || pathNodeIds.length < 2) return [];

  const liensGeo = options?.liensGeo ?? releve_liens.value;
  const appuis = indexReleveAppuis(
    options?.appuisGeo ?? releve_appuis.value,
    options?.defaultInsee
  );
  const byId = new Map(appuis.map((a) => [a.normId, a]));
  const features: Feature[] = [];

  for (let i = 0; i < pathNodeIds.length - 1; i += 1) {
    const u = pathNodeIds[i];
    const v = pathNodeIds[i + 1];
    const linkGeom = findLienGeometryBetween(u, v, liensGeo);
    if (linkGeom) {
      features.push({
        type: "Feature",
        properties: { _kind: "troncon-path", segment: i, from: u, to: v },
        geometry: linkGeom,
      });
      continue;
    }
    const pu = byId.get(u)?.coordinates;
    const pv = byId.get(v)?.coordinates;
    if (pu && pv) {
      features.push({
        type: "Feature",
        properties: { _kind: "troncon-path", segment: i, from: u, to: v, _fallback: true },
        geometry: { type: "LineString", coordinates: [pu, pv] },
      });
    }
  }
  return features;
}

export function buildTronconPathFeatureCollection(
  pathNodeIds: string[],
  options?: { liensGeo?: unknown; appuisGeo?: unknown; defaultInsee?: string }
): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: buildTronconPathFeatures(pathNodeIds, options),
  };
}

/** Coordonnées WGS84 pour zoomer sur une rupture de tronçon (départ / arrivée). */
export function getTronconFailureZoomCoordinates(
  depart: AnchorEndpoint | null | undefined,
  arrivee: AnchorEndpoint | null | undefined,
  options?: { appuisGeo?: unknown; defaultInsee?: string }
): [number, number][] {
  const coords: [number, number][] = [];
  const defaultInsee = options?.defaultInsee;

  if (depart?.insee && (depart.label || depart.id)) {
    const hit = findReleveAppuiByTerrainRef(
      {
        insee: normalizeInseeCode(depart.insee),
        nom: String(depart.label || depart.id).trim(),
      },
      options?.appuisGeo,
      defaultInsee
    );
    if (hit) coords.push(hit.coordinates);
    else if (depart.coordinates?.length === 2) {
      coords.push([depart.coordinates[0], depart.coordinates[1]]);
    }
  }

  if (arrivee?.insee && (arrivee.label || arrivee.id)) {
    const hit = findReleveAppuiByTerrainRef(
      {
        insee: normalizeInseeCode(arrivee.insee),
        nom: String(arrivee.label || arrivee.id).trim(),
      },
      options?.appuisGeo,
      defaultInsee
    );
    if (hit) coords.push(hit.coordinates);
    else if (arrivee.coordinates?.length === 2) {
      coords.push([arrivee.coordinates[0], arrivee.coordinates[1]]);
    }
  }

  return coords;
}
